import { useNodeId } from "@xyflow/react";
import { Suspense } from "react";
import { Button, Colors } from "@zapier/design-system";
import { Icon } from "@zapier/zinnia-icons";
import { useAtomValue } from "jotai";
import {
  assetTypeAndLinkAtomFamily,
  assetAccessAtomFamily_SUSPENDS,
} from "@zapier/canvas-state";

import {
  DARK_COLOR,
  getContrastingTextColor,
} from "../Canvas/nodeTypes/AssetNodeSkeleton/utils";
import { ConditionalTooltip } from "../ConditionalTooltip";
import styles from "./AssetLink.module.css";
import { useOpenAsset } from "../../hooks/useOpenAsset";

type Props = {
  assetId: string;
  assetName: string | undefined;
  interfaceProjectId: string | undefined;
  color: string;
};

function SuspendedComponent({
  assetId,
  assetName,
  interfaceProjectId,
  color,
}: Props) {
  const openAsset = useOpenAsset();
  const nodeId = useNodeId();

  const { assetType, assetLink } = useAtomValue(
    assetTypeAndLinkAtomFamily(nodeId!),
  );

  const { isAssetForbidden } = useAtomValue(
    assetAccessAtomFamily_SUSPENDS({
      assetId,
      interfaceProjectId,
      assetType,
    }),
  );

  if (!assetLink) {
    return null;
  }

  // Ensures the icon is visible since the icon color changes based on the node color
  const contrastingBackgroundColor =
    getContrastingTextColor(color!, true) === DARK_COLOR
      ? Colors.UiPrimaryWeakest
      : Colors.UiPrimaryStrongest;

  return (
    <ConditionalTooltip
      content={`Open ${assetName}`}
      isViewOnlyMode={isAssetForbidden}
    >
      <div className={styles.root}>
        <Button
          size="small"
          iconPosition="start"
          onPress={() => {
            openAsset({ url: assetLink, assetType, location: "node" });
          }}
          isDisabled={isAssetForbidden}
          variant="ghost"
          style={
            {
              color: getContrastingTextColor(
                isAssetForbidden ? Colors.GrayWarm3 : color!,
                true,
              ),
              "--hover-bg-color": contrastingBackgroundColor,
            } as React.CSSProperties
          }
        >
          <Icon name="arrowOffsite" size={16} />
        </Button>
      </div>
    </ConditionalTooltip>
  );
}

export function AssetLink({
  assetId,
  assetName,
  interfaceProjectId,
  color,
}: Props) {
  return (
    <Suspense fallback={null}>
      <SuspendedComponent
        assetId={assetId}
        assetName={assetName}
        interfaceProjectId={interfaceProjectId}
        color={color}
      />
    </Suspense>
  );
}
